(function () {
  'use strict';

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function touchDistance(a, b) {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  function touchMidpoint(a, b) {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  class RiniyaProductGallery {
    constructor(root) {
      this.root = root;
      this.modalId = root.getAttribute('data-gallery-modal-id');
      this.modal = this.modalId ? document.getElementById(this.modalId) : null;

      if (!this.modal) {
        return;
      }

      this.mainSlides = Array.from(root.querySelectorAll('.product_img_top [data-media-index]'));
      this.thumbSlides = Array.from(root.querySelectorAll('.product_slider_img [data-media-index]'));
      this.modalSlides = Array.from(this.modal.querySelectorAll('[data-rb-gallery-modal-slide]'));
      this.modalThumbs = Array.from(this.modal.querySelectorAll('[data-rb-gallery-thumb]'));
      this.prevButton = this.modal.querySelector('[data-rb-gallery-prev]');
      this.nextButton = this.modal.querySelector('[data-rb-gallery-next]');
      this.closeButtons = Array.from(this.modal.querySelectorAll('[data-rb-gallery-close]'));
      this.stage = this.modal.querySelector('[data-rb-gallery-stage]');
      this.currentLabel = this.modal.querySelector('[data-rb-gallery-current]');
      this.totalLabel = this.modal.querySelector('[data-rb-gallery-total]');

      this.activeIndex = 0;
      this.savedBodyOverflow = '';
      this.lastFocusedElement = null;
      this.isOpen = false;
      this.ignoreClickUntil = 0;
      this.mainClickGuardUntil = 0;
      this.pendingSyncIndex = 0;

      this.zoom = { scale: 1, x: 0, y: 0 };
      this.drag = { active: false, startX: 0, startY: 0, originX: 0, originY: 0, didDrag: false };
      this.touch = {
        mode: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        startPanX: 0,
        startPanY: 0,
        startScale: 1,
        startDistance: 0,
        startMidpoint: null,
        moved: false,
        tapAt: 0,
        tapX: 0,
        tapY: 0
      };

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);

      this.bindMainSlides();
      this.bindModal();
      this.showSlide(0, { syncMain: false, resetZoom: true });
    }

    bindMainSlides() {
      this.mainSlides.forEach((slide) => {
        slide.addEventListener(
          'touchstart',
          (event) => {
            if (!event.touches.length) {
              return;
            }

            slide.dataset.rbTouchStartX = String(event.touches[0].clientX);
            slide.dataset.rbTouchStartY = String(event.touches[0].clientY);
            slide.dataset.rbTouchMoved = 'false';
          },
          { passive: true }
        );

        slide.addEventListener(
          'touchmove',
          (event) => {
            if (!event.touches.length) {
              return;
            }

            const startX = parseFloat(slide.dataset.rbTouchStartX || '0');
            const startY = parseFloat(slide.dataset.rbTouchStartY || '0');
            const deltaX = event.touches[0].clientX - startX;
            const deltaY = event.touches[0].clientY - startY;

            if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
              slide.dataset.rbTouchMoved = 'true';
            }
          },
          { passive: true }
        );

        slide.addEventListener(
          'touchend',
          () => {
            if (slide.dataset.rbTouchMoved === 'true') {
              this.mainClickGuardUntil = Date.now() + 300;
            }
          },
          { passive: true }
        );

        slide.addEventListener('click', (event) => {
          if (Date.now() < this.mainClickGuardUntil) {
            return;
          }

          if (this.shouldIgnoreTrigger(event.target)) {
            return;
          }

          event.preventDefault();
          this.open(this.getMediaIndex(slide));
        });

        slide.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          if (this.shouldIgnoreTrigger(event.target)) {
            return;
          }

          event.preventDefault();
          this.open(this.getMediaIndex(slide));
        });
      });
    }

    bindModal() {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.showSlide(this.activeIndex - 1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.showSlide(this.activeIndex + 1);
        });
      }

      this.modalThumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.showSlide(this.getMediaIndex(thumb));
        });
      });

      this.stage.addEventListener('click', (event) => {
        if (Date.now() < this.ignoreClickUntil) {
          return;
        }

        const zoomSurface = event.target.closest('[data-rb-zoom-surface]');

        if (!zoomSurface || this.drag.didDrag) {
          this.drag.didDrag = false;
          return;
        }

        const rect = zoomSurface.getBoundingClientRect();
        this.toggleZoom(event.clientX - rect.left, event.clientY - rect.top);
      });

      this.stage.addEventListener(
        'wheel',
        (event) => {
          const zoomSurface = event.target.closest('[data-rb-zoom-surface]');

          if (!zoomSurface) {
            return;
          }

          event.preventDefault();
          const rect = zoomSurface.getBoundingClientRect();
          const nextScale = this.zoom.scale + (event.deltaY < 0 ? 0.35 : -0.35);
          this.setZoom(nextScale, event.clientX - rect.left, event.clientY - rect.top);
        },
        { passive: false }
      );

      this.stage.addEventListener('mousedown', (event) => {
        const zoomSurface = event.target.closest('[data-rb-zoom-surface]');

        if (!zoomSurface || this.zoom.scale <= 1) {
          return;
        }

        event.preventDefault();
        this.drag.active = true;
        this.drag.startX = event.clientX;
        this.drag.startY = event.clientY;
        this.drag.originX = this.zoom.x;
        this.drag.originY = this.zoom.y;
        this.drag.didDrag = false;
        this.getActiveSlide().classList.add('is-panning');
      });

      this.stage.addEventListener(
        'touchstart',
        (event) => {
          const zoomSurface = event.target.closest('[data-rb-zoom-surface]');
          const touches = event.touches;

          if (!touches.length) {
            return;
          }

          // two fingers on the image = a deliberate pinch-to-zoom gesture
          if (touches.length === 2 && zoomSurface) {
            const rect = zoomSurface.getBoundingClientRect();
            const midpoint = touchMidpoint(touches[0], touches[1]);
            this.touch.mode = 'pinch';
            this.touch.startDistance = touchDistance(touches[0], touches[1]) || 1;
            this.touch.startScale = this.zoom.scale;
            this.touch.startMidpoint = { x: midpoint.x - rect.left, y: midpoint.y - rect.top };
            this.touch.moved = false;
            return;
          }

          const touch = touches[0];

          // a second tap landing near the first one, soon after it, is a
          // deliberate double-tap-to-zoom — not an accidental trigger, since
          // it takes two intentional taps in the same spot
          const now = Date.now();
          if (
            zoomSurface &&
            now - this.touch.tapAt < 320 &&
            Math.abs(touch.clientX - this.touch.tapX) < 30 &&
            Math.abs(touch.clientY - this.touch.tapY) < 30
          ) {
            const rect = zoomSurface.getBoundingClientRect();
            this.toggleZoom(touch.clientX - rect.left, touch.clientY - rect.top);
            this.touch.tapAt = 0;
            this.touch.mode = null;
            this.ignoreClickUntil = Date.now() + 350;
            return;
          }
          this.touch.tapAt = now;
          this.touch.tapX = touch.clientX;
          this.touch.tapY = touch.clientY;

          // already zoomed in: one finger pans around the photo instead of
          // swiping to the next one
          this.touch.mode = this.zoom.scale > 1 && zoomSurface ? 'pan' : 'swipe';
          this.touch.startX = touch.clientX;
          this.touch.startY = touch.clientY;
          this.touch.lastX = touch.clientX;
          this.touch.lastY = touch.clientY;
          this.touch.startPanX = this.zoom.x;
          this.touch.startPanY = this.zoom.y;
          this.touch.moved = false;
        },
        { passive: false }
      );

      this.stage.addEventListener(
        'touchmove',
        (event) => {
          if (!this.touch.mode) {
            return;
          }

          if (this.touch.mode === 'pinch') {
            if (event.touches.length < 2) {
              return;
            }

            event.preventDefault();
            const distance = touchDistance(event.touches[0], event.touches[1]) || this.touch.startDistance;
            const nextScale = this.touch.startScale * (distance / this.touch.startDistance);
            this.setZoom(nextScale, this.touch.startMidpoint.x, this.touch.startMidpoint.y);
            this.touch.moved = true;
            return;
          }

          if (!event.touches.length) {
            return;
          }

          const touch = event.touches[0];
          const deltaX = touch.clientX - this.touch.startX;
          const deltaY = touch.clientY - this.touch.startY;

          this.touch.lastX = touch.clientX;
          this.touch.lastY = touch.clientY;
          this.touch.moved = this.touch.moved || Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8;

          if (this.touch.mode === 'pan' && this.zoom.scale > 1) {
            this.zoom.x = this.touch.startPanX + deltaX;
            this.zoom.y = this.touch.startPanY + deltaY;
            this.constrainPan();
            this.renderZoom();
            event.preventDefault();
            return;
          }

          if (this.touch.mode === 'swipe' && Math.abs(deltaX) > Math.abs(deltaY)) {
            event.preventDefault();
          }
        },
        { passive: false }
      );

      this.stage.addEventListener(
        'touchend',
        (event) => {
          if (!this.touch.mode) {
            return;
          }

          const mode = this.touch.mode;

          if (mode === 'pinch') {
            if (this.zoom.scale <= 1.05) {
              this.resetZoom();
            } else {
              this.constrainPan();
              this.renderZoom();
            }
            this.touch.mode = null;
            this.touch.moved = false;
            this.ignoreClickUntil = Date.now() + 350;
            return;
          }

          const deltaX = this.touch.lastX - this.touch.startX;
          const deltaY = this.touch.lastY - this.touch.startY;

          this.ignoreClickUntil = Date.now() + 350;

          if (
            mode === 'swipe' &&
            this.touch.moved &&
            Math.abs(deltaX) > 50 &&
            Math.abs(deltaX) > Math.abs(deltaY) * 1.2
          ) {
            this.showSlide(this.activeIndex + (deltaX < 0 ? 1 : -1));
          }

          this.touch.mode = null;
          this.touch.moved = false;
        },
        { passive: false }
      );

      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    }

    shouldIgnoreTrigger(target) {
      return Boolean(target.closest('video, iframe, model-viewer, [data-shopify-xr], button:not(.product-single__thumbnail)'));
    }

    getMediaIndex(element) {
      const index = parseInt(element.getAttribute('data-media-index') || element.getAttribute('data-rb-gallery-index'), 10);
      return Number.isNaN(index) ? 0 : index;
    }

    open(index) {
      this.lastFocusedElement = document.activeElement;
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.modal.hidden = false;
      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
      });
      this.modal.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      this.showSlide(index, { syncMain: false, resetZoom: true });
    }

    close() {
      if (!this.isOpen) {
        return;
      }

      this.resetZoom();
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.savedBodyOverflow;
      this.isOpen = false;
      this.syncMainGallery(this.pendingSyncIndex);

      window.setTimeout(() => {
        if (!this.isOpen) {
          this.modal.hidden = true;
        }
      }, 240);

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    showSlide(index, options) {
      const config = Object.assign({ syncMain: true, resetZoom: true }, options || {});
      const slideCount = this.modalSlides.length;

      if (!slideCount) {
        return;
      }

      const nextIndex = ((index % slideCount) + slideCount) % slideCount;

      if (config.resetZoom) {
        this.resetZoom();
      }

      this.pauseMedia(this.activeIndex);
      this.activeIndex = nextIndex;
      this.pendingSyncIndex = nextIndex;

      this.modalSlides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.modalThumbs.forEach((thumb, thumbIndex) => {
        thumb.classList.toggle('is-active', thumbIndex === nextIndex);
      });

      if (this.currentLabel) {
        this.currentLabel.textContent = String(nextIndex + 1);
      }

      if (this.totalLabel) {
        this.totalLabel.textContent = String(slideCount);
      }

      const activeThumb = this.modalThumbs[nextIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      if (config.syncMain) {
        this.syncMainGallery(nextIndex);
      }

      this.renderZoom();
    }

    syncMainGallery(index) {
      const slider = this.root.querySelector('.slider-for');
      const thumbs = this.root.querySelector('.slider-nav');

      if (window.jQuery && window.jQuery.fn && slider && window.jQuery(slider).hasClass('slick-initialized')) {
        window.jQuery(slider).slick('slickGoTo', index);

        if (thumbs && window.jQuery(thumbs).hasClass('slick-initialized')) {
          window.jQuery(thumbs).slick('slickGoTo', index);
        }

        return;
      }

      const fallbackThumb = this.thumbSlides[index];
      if (fallbackThumb) {
        fallbackThumb.click();
      }
    }

    pauseMedia(index) {
      const slide = this.modalSlides[index];

      if (!slide) {
        return;
      }

      slide.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    }

    getActiveSlide() {
      return this.modalSlides[this.activeIndex];
    }

    getActiveZoomImage() {
      const slide = this.getActiveSlide();
      return slide ? slide.querySelector('[data-rb-zoom-image]') : null;
    }

    getActiveZoomSurface() {
      const slide = this.getActiveSlide();
      return slide ? slide.querySelector('[data-rb-zoom-surface]') : null;
    }

    toggleZoom(originX, originY) {
      const activeImage = this.getActiveZoomImage();

      if (!activeImage) {
        return;
      }

      this.setZoom(this.zoom.scale > 1 ? 1 : 2.4, originX, originY);
    }

    setZoom(nextScale, originX, originY) {
      const zoomSurface = this.getActiveZoomSurface();
      const activeImage = this.getActiveZoomImage();

      if (!zoomSurface || !activeImage) {
        return;
      }

      const rect = zoomSurface.getBoundingClientRect();
      const previousScale = this.zoom.scale;
      const clampedScale = clamp(nextScale, 1, 4);

      if (clampedScale === 1) {
        this.resetZoom();
        return;
      }

      const pointerX = typeof originX === 'number' ? originX : rect.width / 2;
      const pointerY = typeof originY === 'number' ? originY : rect.height / 2;
      const centerX = pointerX - rect.width / 2;
      const centerY = pointerY - rect.height / 2;
      const scaleRatio = clampedScale / previousScale;

      this.zoom.x = centerX - (centerX - this.zoom.x) * scaleRatio;
      this.zoom.y = centerY - (centerY - this.zoom.y) * scaleRatio;
      this.zoom.scale = clampedScale;
      this.constrainPan();
      this.renderZoom();
    }

    constrainPan() {
      const zoomSurface = this.getActiveZoomSurface();

      if (!zoomSurface || this.zoom.scale <= 1) {
        this.zoom.x = 0;
        this.zoom.y = 0;
        return;
      }

      const rect = zoomSurface.getBoundingClientRect();
      const maxX = (rect.width * (this.zoom.scale - 1)) / 2;
      const maxY = (rect.height * (this.zoom.scale - 1)) / 2;

      this.zoom.x = clamp(this.zoom.x, -maxX, maxX);
      this.zoom.y = clamp(this.zoom.y, -maxY, maxY);
    }

    renderZoom() {
      const slide = this.getActiveSlide();
      const activeImage = this.getActiveZoomImage();

      if (!slide || !activeImage) {
        return;
      }

      slide.classList.toggle('is-zoomed', this.zoom.scale > 1);
      activeImage.style.transform = 'translate3d(' + this.zoom.x + 'px, ' + this.zoom.y + 'px, 0) scale(' + this.zoom.scale + ')';
    }

    resetZoom() {
      const slide = this.getActiveSlide();
      const activeImage = this.getActiveZoomImage();

      this.zoom.scale = 1;
      this.zoom.x = 0;
      this.zoom.y = 0;

      if (slide) {
        slide.classList.remove('is-zoomed', 'is-panning');
      }

      if (activeImage) {
        activeImage.style.transform = '';
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showSlide(this.activeIndex - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showSlide(this.activeIndex + 1);
      }
    }

    handleMouseMove(event) {
      if (!this.drag.active || this.zoom.scale <= 1) {
        return;
      }

      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;

      this.zoom.x = this.drag.originX + deltaX;
      this.zoom.y = this.drag.originY + deltaY;
      this.drag.didDrag = this.drag.didDrag || Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;
      this.constrainPan();
      this.renderZoom();
    }

    handleMouseUp() {
      if (!this.drag.active) {
        return;
      }

      this.drag.active = false;
      this.getActiveSlide().classList.remove('is-panning');

      if (this.drag.didDrag) {
        this.ignoreClickUntil = Date.now() + 180;
      }
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-rb-product-gallery]').forEach(function (root) {
      if (!root.rbProductGallery) {
        root.rbProductGallery = new RiniyaProductGallery(root);
      }
    });
  });
})();
